import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CalendarClock, FileUp, History, ScrollText, Undo2 } from 'lucide-react';
import type { LocalUpdateStatusDto, ScheduledUpdateDto, UpdateLogDto, UploadedUpdateDto } from '@his/shared';
import { del, get, post, uploadWithProgress } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { dateTime } from '@/lib/utils';
import { UPDATE_STATUS_KEY } from '@/lib/updates-api';
import { Dialog } from '@/components/ui/layout';
import { Alert, Badge, Button, DateTimePicker, Field, Input } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';

function localInputValue(date: Date): string {
  const offset = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
}

function nextTwoAm(now: Date = new Date()): Date {
  const next = new Date(now);
  next.setHours(2, 0, 0, 0);
  if (next.getTime() <= now.getTime() + 5 * 60_000) next.setDate(next.getDate() + 1);
  return next;
}

function ScheduleUpdate({ status, uploaded }: { status: LocalUpdateStatusDto; uploaded: UploadedUpdateDto | null }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const version = status.pendingUpdate?.version ?? uploaded?.version ?? null;
  const [when, setWhen] = useState(() => localInputValue(nextTwoAm()));
  const scheduled = status.scheduledUpdate ?? null;
  const refresh = () => void queryClient.invalidateQueries({ queryKey: UPDATE_STATUS_KEY });

  const schedule = useMutation({
    mutationFn: () => post<ScheduledUpdateDto>('/updates/schedule', { version, at: new Date(when).toISOString() }),
    onSuccess: (result) => {
      toast.success(tr('admin', 'update_scheduled'), tr('admin', 'version_x_installs_at_x_everyone_signed_in_is', { version: result.version, at: dateTime(result.at) }));
      refresh();
    },
    onError: (error) => toast.error(tr('admin', 'could_not_schedule_the_update'), describeError(error)),
  });
  const cancel = useMutation({
    mutationFn: () => del('/updates/schedule'),
    onSuccess: () => {
      toast.success(tr('admin', 'scheduled_update_cancelled'));
      refresh();
    },
    onError: (error) => toast.error(tr('admin', 'could_not_cancel_the_schedule'), describeError(error)),
  });

  if (scheduled) {
    return (
      <div className="flex flex-wrap items-center gap-2 rounded-md border bg-muted/40 p-3 text-sm">
        <CalendarClock className="h-4 w-4 text-primary" aria-hidden />
        <span>
          {tr('admin', 'version')} <span className="font-mono">{scheduled.version}</span> {tr('admin', 'installs_at')} {dateTime(scheduled.at)}
        </span>
        {scheduled.automatic && <Badge variant="warning">{tr('admin', 'required_scheduled_automatically')}</Badge>}
        <Button size="sm" variant="outline" className="ms-auto" loading={cancel.isPending} onClick={() => cancel.mutate()}>
          {tr('admin', 'cancel_schedule')}
        </Button>
      </div>
    );
  }
  if (!version) return null;
  return (
    <div className="flex flex-wrap items-end gap-2">
      <Field label={tr('admin', 'schedule_version_x_for', { version: version })}>
        <DateTimePicker value={when} onChange={(value) => setWhen(value)} />
      </Field>
      <Button variant="outline" loading={schedule.isPending} disabled={!when} onClick={() => schedule.mutate()}>
        <CalendarClock className="h-4 w-4" /> {tr('admin', 'schedule_update')}
      </Button>
    </div>
  );
}

function UploadPackage({ onUploaded }: { onUploaded: (result: UploadedUpdateDto) => void }) {
  const toast = useToast();
  const [file, setFile] = useState<File | null>(null);
  const [progress, setProgress] = useState<number | null>(null);
  const upload = async () => {
    if (!file) return;
    const form = new FormData();
    form.append('file', file);
    setProgress(0);
    try {
      const result = await uploadWithProgress<UploadedUpdateDto>('/updates/upload', form, setProgress);
      toast.success(tr('admin', 'version_x_verified', { version: result.version }), tr('admin', 'apply_it_from_the_server_or_schedule_it'));
      onUploaded(result);
      setFile(null);
    } catch (error) {
      toast.error(tr('admin', 'the_package_was_not_accepted'), describeError(error));
    } finally {
      setProgress(null);
    }
  };
  return (
    <div className="flex flex-wrap items-end gap-2">
      <Field label={tr('admin', 'update_from_a_file_lpk_up_to_4_gb')}>
        <Input type="file" accept=".lpk" onChange={(event) => setFile(event.target.files?.[0] ?? null)} />
      </Field>
      <Button variant="outline" disabled={!file || progress !== null} loading={progress !== null} onClick={() => void upload()}>
        <FileUp className="h-4 w-4" /> {progress !== null ? tr('admin', 'uploading_x', { value: Math.round(progress * 100) }) : tr('admin', 'upload_and_check')}
      </Button>
    </div>
  );
}

function RollbackDialog({ onClose }: { onClose: () => void }) {
  const toast = useToast();
  const rollback = useMutation({
    mutationFn: () => post<{ started: boolean }>('/updates/rollback'),
    onSuccess: () => {
      toast.success(tr('admin', 'rolling_back'), tr('admin', 'the_server_restarts_on_the_previous_version_in'));
      onClose();
    },
    onError: (error) => toast.error(tr('admin', 'could_not_roll_back'), describeError(error)),
  });
  return (
    <Dialog
      open
      onClose={onClose}
      title={tr('admin', 'go_back_to_the_previous_version')}
      description={tr('admin', 'a_copy_of_the_current_database_is_saved_first')}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {tr('admin', 'keep_this_version')}
          </Button>
          <Button variant="destructive" loading={rollback.isPending} onClick={() => rollback.mutate()}>
            <Undo2 className="h-4 w-4" /> {tr('admin', 'roll_back_and_restart')}
          </Button>
        </>
      }
    >
      <Alert variant="warning">{tr('admin', 'everyone_signed_in_loses_their_connection_for_a')}</Alert>
    </Dialog>
  );
}

function UpdateLog() {
  const [log, setLog] = useState<UpdateLogDto | null>(null);
  const [loading, setLoading] = useState(false);
  const load = () => {
    setLoading(true);
    get<UpdateLogDto>('/updates/log')
      .then(setLog)
      .catch(() => setLog({ file: null, lines: ['The update log could not be read.'] }))
      .finally(() => setLoading(false));
  };
  return (
    <div className="space-y-2">
      <Button variant="ghost" size="sm" loading={loading} onClick={load}>
        <ScrollText className="h-4 w-4" /> {log ? tr('admin', 'refresh_the_update_log') : tr('admin', 'show_the_update_log')}
      </Button>
      {log && (
        <pre className="max-h-64 overflow-auto whitespace-pre-wrap rounded-md border bg-muted/40 p-2 font-mono text-[11px] leading-4">
          {log.file ? `${log.file}\n\n` : ''}
          {log.lines.length ? log.lines.join('\n') : tr('admin', 'no_update_has_been_run_on_this_server_yet')}
        </pre>
      )}
    </div>
  );
}

export function UpdateManagement({ status }: { status: LocalUpdateStatusDto }) {
  const [uploaded, setUploaded] = useState<UploadedUpdateDto | null>(null);
  const [rollingBack, setRollingBack] = useState(false);
  const fromHost = status.fromServerHost === true;
  return (
    <div className="space-y-4 border-t pt-4">
      {!fromHost && (
        <p className="text-xs text-muted-foreground">
          {tr('admin', 'update_now_cancel_and_roll_back_work_only_from')}
        </p>
      )}
      <ScheduleUpdate status={status} uploaded={uploaded} />
      <UploadPackage onUploaded={setUploaded} />
      {status.rollbackAvailable && (
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" disabled={!fromHost} onClick={() => setRollingBack(true)}>
            <History className="h-4 w-4" /> {tr('admin', 'roll_back_to_the_previous_version')}
          </Button>
          {!fromHost && <span className="text-xs text-muted-foreground">{tr('admin', 'from_the_server_only')}</span>}
        </div>
      )}
      <UpdateLog />
      {rollingBack && <RollbackDialog onClose={() => setRollingBack(false)} />}
    </div>
  );
}
